import breakPoints from 'helpers/Styles';
import Image from 'next/image';
import { RichText } from 'prismic-reactjs';
import PropTypes from 'prop-types';
import cn from 'classnames';

const ImageWithBoldTitle = ({ block, primaryFontColor, secondaryFontColor }) => {
  const { primary } = block;
  const title = primary?.block_title;
  const titleColour = primary?.block_title_colour
    ? primary.block_title_colour
    : primaryFontColor;
  const description = primary?.block_description;
  const descriptionColour = primary?.block_description_colour
    ? primary.block_description_colour
    : secondaryFontColor;
  const imageUrl = primary?.block_image?.url;
  const imageMobile = primary?.block_image_mobile?.url;
  const videoSource = primary?.video_source;
  const contentPositionMobile = primary?.content_position_mobile;

  return (
    <>
      <div className="image-with-bold-title">
        {videoSource ? (
          <div className="video">
            <video loop muted autoPlay playsInline>
              <source src={videoSource} type="video/mp4" />
            </video>
          </div>
        ) : (
          <>
            <div className="image_desktop">
              <Image draggable={false}
                layout="responsive"
                src={imageUrl}
                alt="Picture of block"
                width={1920}
                height={780}
              />
            </div>

            <div className="image_mobile">
              <Image draggable={false}
                src={imageMobile}
                alt="Picture of block"
                width={primary?.block_image_mobile?.dimensions?.width || 780}
                height={primary?.block_image_mobile?.dimensions?.height || 780}
              />
            </div>
          </>
        )}

        <div className="contents">
          {title && (
            <div
              className={cn('title', {
                title_static: contentPositionMobile === 'Below Image',
                title_absolute: contentPositionMobile === 'On Top',
              })}
            >
              {title}
            </div>
          )}
          {description && (
            <div
              className={cn('description', {
                description_static: contentPositionMobile === 'Below Image',
                description_absolute: contentPositionMobile === 'On Top',
              })}
            >
              {RichText.render(description)}
            </div>
          )}
        </div>
      </div>
      <style jsx>
        {`
          .image-with-bold-title {
            position: relative;
            background-color: inherit;
          }
          .contents {
            width: 100%;
            height: 100%;
            display: flex;
            justify-content: center;
            align-items: center;
          }
          .title {
            color: ${titleColour};
          }
          .description {
            color: ${descriptionColour};
          }
          .image_mobile {
            display: none;
          }
          .video {
            max-width: 100%;
            height: auto;
            max-height: 720px;
          }
          video {
            width: 100%;
            height: auto;
            object-fit: contain;
          }
          @media (min-width: ${breakPoints.medium}) {
            .title {
              position: absolute;
              top: 20%;
              font-family: 'big_noodle_titling';
              font-size: 101px;
              letter-spacing: 0;
              line-height: 101px;
              margin: 0;
              text-align: center;
            }
            .description {
              position: absolute;
              top: 35%;
              width: 50%;
              margin: 0 auto;
              font-family: 'Roboto-Medium';
              font-size: 30px;
              letter-spacing: 0;
              line-height: 37px;
              text-align: justify;
              padding: 20px 0;
            }
          }
          @media (min-width: 1500px) {
            .video {
              max-height: 920px;
            }
          }
          @media (max-width: ${breakPoints.large}) {
            .title {
              font-size: 70px;
              letter-spacing: 0;
              line-height: 70px;
            }
            .description {
              font-size: 21px;
              letter-spacing: 0;
              line-height: 26px;
            }
          }

          @media (max-width: ${breakPoints.medium}) {
            .contents {
              margin-top: 20px;
              flex-direction: column;
              align-items: unset;
              justify-content: unset;
              padding: 0 20px;
            }
            .title_static {
              font-family: 'big_noodle_titling';
              font-size: 49px;
              line-height: 49px;
              position: static;
              text-align: center;
            }
            .description_static {
              margin: 20px 0;
              font-family: 'Roboto-Medium';
              font-size: 15px;
              line-height: 19px;
              position: static;
              width: 100%;
              text-align: justify;
            }
            .title_absolute {
              font-family: 'big_noodle_titling';
              font-size: 49px;
              line-height: 49px;
              position: absolute;
              top: 10%;
              width: 100%;
              text-align: center;
            }
            .description_absolute {
              font-family: 'Roboto-Medium';
              font-size: 15px;
              line-height: 19px;
              position: absolute;
              width: 90%;
              top: 40%;
              text-align: justify;
            }
          }
          @media (max-width: ${breakPoints.small}) {
            .title {
              max-width: 344px;
              font-size: 49px;
              line-height: 49px;
            }
            .description {
              font-size: 15px;
              letter-spacing: 0;
              line-height: 19px;
            }
            .image_desktop {
              display: none;
            }
            .image_mobile {
              display: block;
            }
            .video {
              height: auto;
            }
            video {
              height: auto;
            }
          }
        `}
      </style>
    </>
  );
};

ImageWithBoldTitle.propTypes = {
  block: PropTypes.shape({
    primary: PropTypes.shape({
      block_title: PropTypes.string,
      block_description: PropTypes.string,
      block_image: PropTypes.string,
      block_image_mobile: PropTypes.string,
      video_source: PropTypes.string,
      content_position_mobile: PropTypes.string,
    }),
  }),
};

ImageWithBoldTitle.defaultProps = {
  block: null,
};

export default ImageWithBoldTitle;
